import { DialogContent, styled, Switch, Grid, TextField } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { FieldValues, FormProvider, Controller, useForm } from 'react-hook-form';
import { Cell } from 'react-table';
import { ITaxTypesDetails } from 'wisestep-api';
import {
  WsTypography,
  WsTypoVariants,
  WsButton,
  ControlledTextInput,
  WsDrawer,
  WsCheckboxList,
  WsTypoColors,
  Item,
  WsSelect,
  WsDataTable,
  WsDatePicker,
} from 'wisestep-base';

import { IDefaultValue, IInvoiceDataList, IReceiptTypeList } from '../helper/InvocieSettingsHelper';

const checkBoxListOptions: {
  [key: string]: string;
} = {};

export interface IDrawerWithCheckbox {
  openDialog: boolean;
  closeDialog(): void;
  onInput: (data: FieldValues, arrayDirity: [], rowData) => void;
  name?: string;
  extraField?: boolean;
  addPlaceholder1?: string;
  addPlaceholder2?: string;
  isView?: boolean;
  defaultValue?: IDefaultValue;
  isToggledEnabled?: boolean;
  checkToggle?: boolean;
  showViewCheckList?: boolean;
  toggledText?: string;
  checkBoxListData?: ITaxTypesDetails[];
  selectedRecordObjectData?: ITaxTypesDetails[];
  isTaxTypeDrawer?: boolean;
  isTransactionDrawer?: boolean;
  currencyTypeList?: IReceiptTypeList[];
  bankDetailsList?: IReceiptTypeList[];
  noteText?: string;
  defaultTranscationalRowData?: IInvoiceDataList[];
}

export const StyledSwitch = styled(Switch)(() => ({
  top: '0.5vw',
}));

const GridItem = styled(Item)(() => ({
  paddingTop: 0,
  paddingBottom: '10px',
}));

const InvoiceDrawerLayout = ({
  openDialog,
  closeDialog,
  onInput,
  name,
  addPlaceholder1 = 'Enter New Value',
  addPlaceholder2 = 'Enter New Value',
  isView = false,
  isToggledEnabled = false,
  checkToggle = false,
  toggledText,
  checkBoxListData,
  selectedRecordObjectData,
  showViewCheckList,
  defaultValue,
  noteText,
  isTaxTypeDrawer = false,
  isTransactionDrawer = false,
  bankDetailsList,
  currencyTypeList,
  defaultTranscationalRowData = [],
}: IDrawerWithCheckbox) => {
  const methods = useForm({ defaultValues: defaultValue });
  const {
    formState: { dirtyFields },
  } = methods;
  const [showCheckBoxList, setShowCheckBoxList] = useState<boolean>(checkToggle);
  const rowData = useRef<IInvoiceDataList[]>([]);
  const [adjustmentValue, setAdjustmentValue] = useState<number>(0);
  const [amountOsValue, setAmountOsValue] = useState<number>(
    defaultTranscationalRowData[0]?.amountOs ?? 0
  );

  const updateAmount = (e) => {
    const exchangeRateValue = methods.getValues('exchangeRate');
    setAdjustmentValueWithExchangeRate(Number(exchangeRateValue), e.target.value);
  };

  const updateCurrency = (e) => {
    const transcationAmountValue = methods.getValues('transcationAmount');
    if (transcationAmountValue) {
      setAdjustmentValueWithExchangeRate(Number(transcationAmountValue), e.target.value);
    }
  };

  const setAdjustmentValueWithExchangeRate = (exchangeRate: number, transcationAmount: number) => {
    const getAmountWithExchange = (exchangeRate ? exchangeRate : 1) * transcationAmount;
    setAdjustmentValue(getAmountWithExchange);
    setAmountOsValue(defaultTranscationalRowData[0].amountOs - getAmountWithExchange);
  };

  const transcationHeader = [
    {
      Header: 'Invoice ID',
      accessor: 'invoice_code',
      Cell: (cell: Cell) => (
        <TextField
          fullWidth
          name="invoice_code"
          size="small"
          type="text"
          placeholder="invoice_code"
          defaultValue={cell.value}
          inputProps={{ readOnly: true }}
        />
      ),
    },
    {
      Header: 'Invoice Currency',
      accessor: 'invoiceCurrency',
      Cell: (cell: Cell) => (
        <TextField
          fullWidth
          name="invoiceCurrency"
          size="small"
          type="text"
          id={cell.value}
          placeholder="invoiceCurrency"
          defaultValue={cell.value}
          inputProps={{ readOnly: true }}
        />
      ),
    },
    {
      Header: 'Invoice Amount',
      accessor: 'amount',
      Cell: (cell: Cell) => (
        <TextField
          fullWidth
          name="amount"
          size="small"
          type="text"
          id={cell.value}
          placeholder="amount"
          defaultValue={cell.value}
          inputProps={{ readOnly: true }}
        />
      ),
    },
    {
      Header: 'Amount Adj',
      accessor: 'adjustment',
      Cell: (cell: Cell) => (
        <TextField
          fullWidth
          name="Adjustment"
          size="small"
          type="text"
          id={cell.value}
          placeholder="adjustment"
          defaultValue={adjustmentValue}
          inputProps={{ readOnly: true }}
        />
      ),
    },
    {
      Header: 'Amount O/S',
      accessor: 'amountOs',
      Cell: (cell: Cell) => (
        <TextField
          fullWidth
          name="AmountOs"
          size="small"
          type="text"
          id={cell.value}
          placeholder="amountOs"
          defaultValue={amountOsValue}
          inputProps={{ readOnly: true }}
        />
      ),
    },
  ];

  useEffect(() => {
    checkBoxListData?.forEach((data) => (checkBoxListOptions[data.id] = data.name));
    rowData.current = [...defaultTranscationalRowData];
  }, [checkBoxListData, defaultTranscationalRowData]);

  const onSubmit = (data: FieldValues) => {
    const avChangedValueViaDirtyFileds = Object.keys(dirtyFields).toString().split(',');
    onInput(data, avChangedValueViaDirtyFileds as [], rowData.current);
  };
  return (
    <WsDrawer
      open={openDialog}
      size={isTransactionDrawer ? 'medium' : 'small'}
      onClose={closeDialog}
      title={name}
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <DialogContent>
            {isTransactionDrawer && (
              <>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={6}>
                    <WsSelect
                      label="Bank"
                      name="bankName"
                      size="medium"
                      selectArray={bankDetailsList ?? []}
                      required
                    />
                  </Grid>
                  <Grid item xs={6} sm={6}>
                    <WsTypography variant={WsTypoVariants.SUB_TITLE}>Transcation Date</WsTypography>
                    <WsDatePicker
                      name="transcationDate"
                      label=""
                      disableFuture={true}
                      size="small"
                      required
                    />
                  </Grid>
                  <Grid item xs={6} sm={6}>
                    <ControlledTextInput
                      fullWidth
                      labelText="Payment Reference"
                      name="paymentReference"
                      size="small"
                      required
                    />
                  </Grid>
                  <Grid item xs={6} sm={6}>
                    <WsSelect
                      label="Currency"
                      name="currencyType"
                      size="small"
                      selectArray={currencyTypeList ?? []}
                      required
                    />
                  </Grid>
                  <Grid item xs={3} sm={3}>
                    <ControlledTextInput
                      fullWidth
                      name="exchangeRate"
                      labelText="Exchange Rate"
                      size="medium"
                      onBlur={updateCurrency}
                    />
                  </Grid>
                  <Grid item xs={4} sm={4}>
                    <ControlledTextInput
                      fullWidth
                      name="transcationAmount"
                      labelText="Amount"
                      size="medium"
                      required
                      onBlur={updateAmount}
                    />
                  </Grid>
                  <Grid item xs={8} sm={10}>
                    <ControlledTextInput
                      fullWidth
                      labelText="Transcation Comments"
                      name="transactionComment"
                      size="medium"
                    />
                  </Grid>

                  <Grid container py={1}>
                    <WsDataTable
                      columns={transcationHeader as []}
                      data={defaultTranscationalRowData as []}
                    />
                  </Grid>
                </Grid>
              </>
            )}
            {isTaxTypeDrawer && (
              <>
                {isView ? (
                  <>
                    <GridItem sx={{ mt: '14px' }}>
                      <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.PRIMARY}>
                        Taxation Legal Name :
                      </WsTypography>
                      <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.BLACK}>
                        {` ${addPlaceholder1}`}
                      </WsTypography>
                    </GridItem>
                    <GridItem sx={{ mt: '14px' }}>
                      <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.PRIMARY}>
                        Taxation Rate :
                      </WsTypography>
                      <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.BLACK}>
                        {` ${addPlaceholder2}`}
                      </WsTypography>
                    </GridItem>
                    <GridItem sx={{ mt: '14px' }}>
                      <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.PRIMARY}>
                        Taxation Application Type :
                      </WsTypography>
                      <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.BLACK}>
                        {` ${toggledText}`}
                      </WsTypography>
                    </GridItem>
                    {showViewCheckList && (
                      <>
                        <GridItem sx={{ mt: '14px' }}>
                          <WsTypography
                            variant={WsTypoVariants.SUB_TITLE}
                            color={WsTypoColors.PRIMARY}
                          >
                            Selected Tax Types :
                          </WsTypography>
                          {selectedRecordObjectData?.map((item, index) => (
                            <WsTypography
                              key={index}
                              variant={WsTypoVariants.SUB_TITLE}
                              color={WsTypoColors.BLACK}
                            >
                              {item}
                            </WsTypography>
                          ))}
                        </GridItem>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <Grid container p={4} justifyItems="center" spacing={1}>
                      <Grid item xs={12} sm={6}>
                        <ControlledTextInput
                          name="fieldOne"
                          size="small"
                          placeholder={addPlaceholder1}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <ControlledTextInput
                          name="fieldTwo"
                          size="small"
                          placeholder={addPlaceholder2}
                          required
                        />
                      </Grid>
                      <GridItem sx={{ mt: '12px' }}>
                        <WsTypography
                          variant={WsTypoVariants.CAPTION}
                          color={WsTypoColors.GREYTEXT}
                        >
                          {noteText}
                        </WsTypography>
                      </GridItem>
                      {isToggledEnabled && (
                        <Grid item container xs={12}>
                          <Grid item xs={10}>
                            <WsTypography variant={WsTypoVariants.SUB_TITLE}>
                              {toggledText}
                            </WsTypography>
                          </Grid>
                          <Grid item xs={2}>
                            <Controller
                              name="toggled"
                              render={() => (
                                <StyledSwitch
                                  checked={showCheckBoxList}
                                  name="toggled"
                                  disabled={checkToggle}
                                  onChange={(_event, checked) => {
                                    methods.setValue('toggled', checked);
                                    setShowCheckBoxList(checked);
                                  }}
                                />
                              )}
                            />
                          </Grid>
                          {showCheckBoxList && (
                            <WsCheckboxList
                              name="applyToTaxes"
                              options={checkBoxListOptions}
                              required
                            />
                          )}
                        </Grid>
                      )}
                    </Grid>
                  </>
                )}
              </>
            )}
            <>
              {!isView && (
                <Grid>
                  <Grid item xs={12} container justifyContent="flex-end">
                    <WsButton type="submit" size="large">
                      Save
                    </WsButton>
                  </Grid>
                </Grid>
              )}
            </>
          </DialogContent>
        </form>
      </FormProvider>
    </WsDrawer>
  );
};

export default InvoiceDrawerLayout;
